// Our pins on top of the basemap. No clustering: every feature is always a dot (a
// circle layer, which costs nothing in label placement), and gains an icon plus a
// label — if there is room — at a zoom that depends on its rank (3 = headline,
// 2 = worth the detour, 1 = the rest; see LayerDef.tiers).
//
// MapLibre places symbols top layer first, so layer order *is* label priority. The
// stack, bottom to top: dots < rank-1 < rank-2 < [town tiers 3–7] < rank-3 < [cities and
// principal towns] < basemap towns above z10 (the bracketed ones are added by towns.ts).
// Within a rank, history is added last so it is placed first.
import type { ExpressionSpecification, GeoJSONSource, Map as MLMap } from "maplibre-gl";
import type { Feature, LayerId, Rank, Site } from "../types";
import { LAYERS, LAYER_ORDER } from "../data/layers";
import { historyIconName, layerIconName } from "./icons";

export const SELECT_SOURCE = "selected";
export const RANKS: Rank[] = [3, 2, 1];
/** Layers in add order for one rank: history last, so it's on top and placed first. */
const ADD_ORDER: LayerId[] = [...LAYER_ORDER].reverse();

export const srcId = (l: LayerId) => `src-${l}`;
export const dotsId = (l: LayerId) => `${l}-dots`;
export const symId = (l: LayerId, r: Rank) => `${l}-r${r}`;
/** Bottom-most rank-3 symbol layer — towns.ts slots the smaller towns in just below it. */
export const FIRST_RANK3_ID = symId(ADD_ORDER[0], 3);

/** Every clickable layer id present on the map (dots + all symbol tiers). */
export function pointLayerIds(map: MLMap): string[] {
  const ids: string[] = [];
  for (const l of LAYER_ORDER) {
    for (const r of RANKS) ids.push(symId(l, r));
    ids.push(dotsId(l));
  }
  return ids.filter((id) => !!map.getLayer(id));
}

function toGeoJSON(feats: Feature[]): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: feats.map((f) => {
      const site = f.layer === "history" ? (f.raw as Site) : null;
      return {
        type: "Feature",
        id: undefined,
        geometry: { type: "Point", coordinates: [f.lon, f.lat] },
        properties: {
          key: f.key,
          layer: f.layer,
          // Map labels use the short map name if there is one, else the English form —
          // it's what road signs and guidebooks say; the Irish name leads in the card.
          name: f.mapName ?? (site ? (site.general_name_en || f.name) : f.name),
          icon: site ? historyIconName(site.periods?.[0]) : layerIconName(f.layer),
          color: f.color,
          rank: f.rank,
          // Lower = placed first. Rank decides; a photo breaks ties.
          prio: -(f.rank * 10 + (site?.photo ? 1 : 0)),
        },
      };
    }),
  };
}

const EMPTY: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

// Icons grow from a 14 px dot at the island view to a 30 px marker at street level;
// 1.0 is the 22 px canvas icon.
const ICON_SIZE: ExpressionSpecification = ["interpolate", ["linear"], ["zoom"], 5, 0.62, 8, 0.8, 10, 1, 12, 1.2, 14, 1.35];
// Text sits just outside the icon: radius in em, a little more as icons grow.
const TEXT_OFFSET: ExpressionSpecification = ["interpolate", ["linear"], ["zoom"], 5, 0.8, 10, 1.05, 14, 1.2];

function textSize(rank: Rank, compact: boolean): ExpressionSpecification {
  const d = (rank === 3 ? 0.75 : rank === 2 ? 0.25 : 0) - (compact ? 1 : 0);
  return ["interpolate", ["linear"], ["zoom"], 5, 10.25 + d, 8, 11.25 + d, 11, 12.75 + d, 14, 14 + d];
}

// A phone's map is about 2.3x narrower than the desk's, so at a given zoom it shows a
// fraction of the places: labels can come in ~1.2 zoom levels (log2 of that) earlier
// before the screen is as busy. Collision placement still decides what actually fits.
const COMPACT_TIER_SHIFT = 1.2;

/**
 * Add sources + layers (idempotent). Call on `load` and after every `style.load`.
 * `compact` trims label sizes and brings label tiers in earlier for narrow (phone)
 * viewports.
 */
export function ensureOverlays(map: MLMap, theme: "light" | "dark", compact = false): void {
  // A crisp white (or near-black) halo: feature names sit *on* the map, where town
  // names — haloed in the land colour, see towns.ts — sit *in* it.
  const halo = theme === "dark" ? "#0f1523" : "#ffffff";

  if (!map.getSource(SELECT_SOURCE)) {
    map.addSource(SELECT_SOURCE, { type: "geojson", data: EMPTY });
    map.addLayer({
      id: "selected-halo",
      type: "circle",
      source: SELECT_SOURCE,
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 14, 14, 22],
        "circle-color": ["get", "color"],
        "circle-opacity": 0.28,
        "circle-stroke-color": ["get", "color"],
        "circle-stroke-width": 2,
        "circle-stroke-opacity": 0.9,
      },
    });
  }

  // Sources and layers are added together; if one is here, all are.
  if (map.getSource(srcId("history"))) return;
  for (const id of LAYER_ORDER) map.addSource(srcId(id), { type: "geojson", data: EMPTY });

  // Dots: everything, always. Not symbols, so they never take part in collision.
  for (const id of ADD_ORDER) {
    map.addLayer({
      id: dotsId(id),
      type: "circle",
      source: srcId(id),
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 2.2, 7, 2.8, 9, 3.6, 11, 4.5],
        "circle-color": ["get", "color"],
        "circle-opacity": 0.92,
        "circle-stroke-color": halo,
        "circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 5, 0.6, 9, 1],
      },
    });
  }

  // Icon + label tiers, lowest rank first so the headline tier ends up on top.
  for (const rank of [1, 2, 3] as Rank[]) {
    for (const id of ADD_ORDER) {
      map.addLayer({
        id: symId(id, rank),
        type: "symbol",
        source: srcId(id),
        minzoom: Math.max(0, LAYERS[id].tiers[rank] - (compact ? COMPACT_TIER_SHIFT : 0)),
        filter: ["==", ["get", "rank"], rank],
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": ICON_SIZE,
          "icon-allow-overlap": true,
          // Below the town view an icon doesn't block a town name (the name may run
          // over a small dot); above it, icons claim their space.
          "icon-ignore-placement": ["step", ["zoom"], true, 10, false],
          "text-field": ["get", "name"],
          "text-font": [rank === 3 ? "Noto Sans Medium" : "Noto Sans Regular"],
          "text-size": textSize(rank, compact),
          "text-variable-anchor": ["left", "right", "top", "bottom"],
          "text-radial-offset": TEXT_OFFSET,
          "text-justify": "auto",
          "text-optional": true,
          "text-max-width": 9,
          "text-padding": 1,
          "symbol-sort-key": ["get", "prio"],
        },
        paint: {
          "text-color": LAYERS[id].labelColor[theme],
          "text-halo-color": halo,
          "text-halo-width": 1.6,
          "text-halo-blur": 0.3,
        },
      });
    }
  }
}

export function setLayerData(map: MLMap, id: LayerId, feats: Feature[]): void {
  const src = map.getSource(srcId(id)) as GeoJSONSource | undefined;
  if (!src) return;
  src.setData(toGeoJSON(feats));
}

export function setSelectedFeature(map: MLMap, f: Feature | null): void {
  const src = map.getSource(SELECT_SOURCE) as GeoJSONSource | undefined;
  if (!src) return;
  src.setData(f ? toGeoJSON([f]) : EMPTY);
}
